/**
 * Service to manage user readings
 */

// Import custom event from UserReadings
import { notifyAuthStateChange } from '../components/UserReadings';

// 后端URL配置 #动态API地址
const getBackendUrl = () => '';

// Generate a unique ID for anonymous users
const getAnonymousId = () => {
  let anonymousId = localStorage.getItem('anonymous_user_id');
  if (!anonymousId) {
    anonymousId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('anonymous_user_id', anonymousId);
  }
  return anonymousId;
};

// Update initializeUser to handle both new and existing users
export const initializeUser = async (userData) => {
  try {
    console.log('Initializing user:', userData);
    
    // Save user data to localStorage immediately to prevent auth issues
    localStorage.setItem('user', JSON.stringify(userData));
    console.log('User data saved to localStorage:', userData);
    
    // Verify the data was saved correctly
    const savedUserJson = localStorage.getItem('user');
    console.log('Retrieved user data from localStorage:', savedUserJson);
    
    if (savedUserJson) {
      try {
        const savedUser = JSON.parse(savedUserJson);
        console.log('Parsed saved user data:', savedUser);
        if (savedUser.id !== userData.id) {
          console.error('User ID mismatch! Saved:', savedUser.id, 'Expected:', userData.id);
        }
      } catch (e) {
        console.error('Error parsing saved user data:', e);
      }
    } else {
      console.error('Failed to save user data to localStorage!');
    }
    
    // Try to initialize user in database
    console.log('Making API call to /login/initialize');
    const response = await fetch(`${getBackendUrl()}/login/initialize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData)
    });
    
    console.log('API response status:', response.status);
    console.log('API response headers:', Object.fromEntries([...response.headers.entries()]));
    
    // Check if response is ok before parsing JSON
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server returned an error:', response.status, errorText);
      throw new Error(`Server error: ${response.status}. Please try again later.`);
    }
    
    // Check content type to avoid parsing HTML as JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Server returned non-JSON response:', text);
      throw new Error('Server returned an invalid response. Please try again later.');
    }
    
    const result = await response.json();
    console.log('DB initialization result:', result);
    
    if (result.success) {
      // Get readings from DB and sync with local storage
      const dbReadings = await getAllReadings(userData.id);
      console.log('Got readings from DB:', dbReadings);
      
      if (dbReadings.success) {
        localStorage.setItem(`readings_${userData.id}`, JSON.stringify(dbReadings.readings));
      }
      
      // Migrate any anonymous readings to this user account
      await migrateAnonymousReadings(userData.id);
      
      // Sync readings with the database
      await syncReadingsOnLogin(userData.id);
      
      // Notify components about auth state change
      notifyAuthStateChange();
    }
    
    return result;
  } catch (error) {
    console.error('Error initializing user:', error);
    // Fall back to localStorage-only mode
    return { 
      success: true, 
      message: 'Using local storage mode due to server connection issue',
      localOnly: true 
    };
  }
};

const getAllReadings = async (userId) => {
  try {
    const response = await fetch(`${getBackendUrl()}/login/getall?user_id=${userId}`);
    
    // Check if response is ok before parsing JSON
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server returned an error:', response.status, errorText);
      throw new Error(`Server error: ${response.status}. Please try again later.`);
    }
    
    // Check content type to avoid parsing HTML as JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Server returned non-JSON response:', text);
      throw new Error('Server returned an invalid response. Please try again later.');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting readings:', error);
    return { success: true, readings: [], localOnly: true };
  }
};

const putReading = async (userId, readingData) => {
  try {
    console.log(`🔄 PUT READING: Making API call to /login/put for user ${userId}`);
    console.log('📤 Reading data being sent:', {
      userId: userId,
      readingData: readingData,
      readingId: readingData.id,
      timestamp: readingData.timestamp
    });

    const response = await fetch(`${getBackendUrl()}/login/put?user_id=${userId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(readingData)
    });

    console.log('📡 API response status:', response.status);
    console.log('📋 API response headers:', Object.fromEntries([...response.headers.entries()]));

    // Check if response is ok before parsing JSON
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ SERVER ERROR:', {
        status: response.status,
        errorText: errorText,
        userId: userId,
        readingId: readingData.id
      });
      throw new Error(`Server error: ${response.status}. Please try again later.`);
    }

    // Check content type to avoid parsing HTML as JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('❌ INVALID RESPONSE TYPE:', {
        contentType: contentType,
        responseText: text,
        userId: userId,
        readingId: readingData.id
      });
      throw new Error('Server returned an invalid response. Please try again later.');
    }

    const result = await response.json();
    console.log('✅ PUT READING RESULT:', {
      success: result.success,
      message: result.message,
      userId: userId,
      readingId: readingData.id,
      storedUnder: `readings_${userId}`,
      fullResult: result
    });

    if (!result.success) {
      console.warn('⚠️ PUT READING FAILED:', {
        userId: userId,
        readingId: readingData.id,
        errorMessage: result.message,
        fullResult: result
      });
    }

    return result;
  } catch (error) {
    console.error('💥 PUT READING EXCEPTION:', {
      error: error.message,
      userId: userId,
      readingId: readingData.id,
      stack: error.stack
    });
    return { success: true, localOnly: true };
  }
};

// Add a function to sanitize reading data for DB
const sanitizeReadingForDB = (reading) => {
  return {
    id: reading.id || `reading_${Date.now()}`,
    timestamp: reading.timestamp || new Date().toISOString(),
    userId: reading.userId,
    question: reading.question || '',
    cards: reading.cards || '',
    framework: reading.framework || '',
    answer: reading.answer || '',
    history: JSON.stringify(reading.history || []), // Convert array to string
    initialStressLevel: reading.initialStressLevel || null,
    finalStressLevel: reading.finalStressLevel || null,
    character: reading.character ? JSON.stringify(reading.character) : null, // Convert object to string
    isAnonymous: reading.isAnonymous || false
  };
};

// Update saveReading to use sanitized data
export const saveReading = async (reading) => {
  console.log('saveReading called with reading:', reading);
  
  const user = getCurrentUser();
  console.log('Current user from getCurrentUser:', user);
  
  const userId = user ? user.id : getAnonymousId();
  console.log('Using userId:', userId, user ? '(logged in user)' : '(anonymous user)');
  
  const readingData = {
    id: reading.id || `reading_${Date.now()}`,
    timestamp: reading.timestamp || new Date().toISOString(),
    userId: userId,
    isAnonymous: !user,
    ...reading
  };
  
  console.log('Prepared reading data:', readingData);
  
  // If user is not logged in, save to local storage only
  if (!user) {
    console.log('Saving reading to local storage for anonymous user');
    const userReadings = getUserReadings();
    const existingIndex = userReadings.findIndex(r => r.id === readingData.id);
    
    if (existingIndex !== -1) {
      console.log('Updating existing reading in local storage:', readingData.id);
      userReadings[existingIndex] = readingData;
    } else {
      console.log('Adding new reading to local storage:', readingData.id);
      userReadings.push(readingData);
    }
    
    localStorage.setItem(`readings_${userId}`, JSON.stringify(userReadings));
    return readingData;
  }
  
  // If user is logged in, save to DB
  try {
    console.log('💾 SAVE READING: Saving to DB for logged in user', {
      userId: userId,
      userEmail: user.email,
      readingId: readingData.id,
      readingType: readingData.framework || 'unknown'
    });

    // Sanitize data before sending to DB
    const sanitizedData = sanitizeReadingForDB(readingData);
    console.log('🔧 Sanitized data for DB:', {
      id: sanitizedData.id,
      userId: sanitizedData.userId,
      question: sanitizedData.question?.substring(0, 50) + '...',
      cards: sanitizedData.cards,
      framework: sanitizedData.framework
    });

    const response = await putReading(userId, sanitizedData);
    console.log('💾 DB SAVE COMPLETE:', {
      success: response.success,
      message: response.message,
      userId: userId,
      readingId: readingData.id,
      localOnly: response.localOnly,
      fullResponse: response
    });

    if (!response.success) {
      console.error('❌ FAILED TO SAVE TO DB:', {
        userId: userId,
        readingId: readingData.id,
        errorMessage: response.message,
        fullResponse: response
      });
      // Fall back to local storage if DB save fails
      const userReadings = getUserReadings();
      const existingIndex = userReadings.findIndex(r => r.id === readingData.id);
      
      if (existingIndex !== -1) {
        userReadings[existingIndex] = readingData;
      } else {
        userReadings.push(readingData);
      }
      
      localStorage.setItem(`readings_${userId}`, JSON.stringify(userReadings));
    } else {
      console.log('Successfully saved reading to DB:', readingData.id);
      // Update local cache with the latest DB data to keep them in sync
      await updateLocalCacheFromDB(userId);
    }
  } catch (error) {
    console.error('Error saving reading to DB:', error);
    // Fall back to local storage if an error occurs
    const userReadings = getUserReadings();
    const existingIndex = userReadings.findIndex(r => r.id === readingData.id);
    
    if (existingIndex !== -1) {
      userReadings[existingIndex] = readingData;
    } else {
      userReadings.push(readingData);
    }
    
    localStorage.setItem(`readings_${userId}`, JSON.stringify(userReadings));
  }
  
  return readingData;
};

// Get all readings for the current user from local storage
export const getUserReadings = () => {
  const user = getCurrentUser();
  const userId = user ? user.id : getAnonymousId();
  
  // For logged-in users, we'll get the synchronized readings from local storage
  // For anonymous users, we'll get their local-only readings
  const readingsJson = localStorage.getItem(`readings_${userId}`);
  const readings = readingsJson ? JSON.parse(readingsJson) : [];
  
  console.log(`Retrieved ${readings.length} readings for ${user ? 'user' : 'anonymous'} ID: ${userId}`);
  return readings;
};

// Get a specific reading by ID
export const getReadingById = (readingId) => {
  const readings = getUserReadings();
  return readings.find(reading => reading.id === readingId);
};

// Delete a reading
export const deleteReading = (readingId) => {
  const user = getCurrentUser();
  const userId = user ? user.id : getAnonymousId();
  
  const readings = getUserReadings();
  const updatedReadings = readings.filter(reading => reading.id !== readingId);
  
  localStorage.setItem(`readings_${userId}`, JSON.stringify(updatedReadings));
  return true;
};

// Save reading data from detailNPC
export const saveDetailNPCReading = (detailNPC) => {
  if (!detailNPC) {
    console.warn('⚠️ saveDetailNPCReading called with null/undefined data');
    return null;
  }

  console.log('🎴 DETAIL NPC READING SAVE STARTED:', {
    id: detailNPC.id,
    cards: detailNPC.cards,
    question: detailNPC.question?.substring(0, 50) + '...',
    framework: detailNPC.framework,
    hasAnswer: !!detailNPC.answer,
    hasHistory: Array.isArray(detailNPC.history) && detailNPC.history.length > 0,
    initialStressLevel: detailNPC.initialStressLevel,
    finalStressLevel: detailNPC.finalStressLevel,
    character: detailNPC.character
  });

  // Extract relevant data from detailNPC
  const readingData = {
    id: detailNPC.id, // Include the ID if it exists
    cards: detailNPC.cards,
    question: detailNPC.question,
    framework: detailNPC.framework,
    answer: detailNPC.answer,
    history: detailNPC.history || [], // Add conversation history
    initialStressLevel: detailNPC.initialStressLevel,
    finalStressLevel: detailNPC.finalStressLevel,
    character: detailNPC.character, // Add character data
    timestamp: detailNPC.timestamp || new Date().toISOString()
  };

  // If we have an ID, this is an update to an existing reading
  if (readingData.id) {
    console.log('🔄 UPDATING EXISTING READING:', {
      readingId: readingData.id,
      userAction: 'feedback_update',
      finalStressLevel: readingData.finalStressLevel
    });
  } else {
    console.log('🆕 CREATING NEW READING');
  }

  const result = saveReading(readingData);
  console.log('🎴 DETAIL NPC READING SAVE COMPLETED:', {
    readingId: readingData.id,
    result: result
  });

  return result;
};

// Migrate anonymous readings to user account after login
export const migrateAnonymousReadings = async (userId) => {
  const anonymousId = localStorage.getItem('anonymous_user_id');
  if (!anonymousId) return;
  
  const anonymousReadingsJson = localStorage.getItem(`readings_${anonymousId}`);
  if (!anonymousReadingsJson) return;
  
  console.log('Migrating anonymous readings to user account:', userId);
  
  const anonymousReadings = JSON.parse(anonymousReadingsJson);
  const userReadingsJson = localStorage.getItem(`readings_${userId}`);
  const userReadings = userReadingsJson ? JSON.parse(userReadingsJson) : [];
  
  // Update the user ID and anonymous flag for each reading
  const migratedReadings = anonymousReadings.map(reading => ({
    ...reading,
    userId: userId,
    isAnonymous: false,
    migratedAt: new Date().toISOString()
  }));
  
  // Combine with existing user readings
  const combinedReadings = [...userReadings, ...migratedReadings];
  
  // Save to user's storage
  localStorage.setItem(`readings_${userId}`, JSON.stringify(combinedReadings));
  
  // Clear anonymous readings
  localStorage.removeItem(`readings_${anonymousId}`);
  
  // Try to sync migrated readings to the database
  try {
    for (const reading of migratedReadings) {
      const sanitizedReading = sanitizeReadingForDB({
        ...reading,
        userId: userId,
        isAnonymous: false
      });
      await putReading(userId, sanitizedReading);
    }
    
    // Update local cache from DB to ensure consistency
    await updateLocalCacheFromDB(userId);
    
    console.log('Successfully migrated and synced anonymous readings to user account');
  } catch (error) {
    console.error('Error syncing migrated readings to DB:', error);
    // Local storage already updated, so this is non-fatal
  }
};

// 修改getCurrentUser以处理新的localStorage键名
export const getCurrentUser = () => {
  console.log('getCurrentUser called');
  
  // 首先检查新的aitarot_user键
  const aitarotUserJson = localStorage.getItem('aitarot_user');
  if (aitarotUserJson) {
    try {
      const user = JSON.parse(aitarotUserJson);
      console.log('Found user in aitarot_user:', user);
      if (user && user.email) {
        return user;
      }
    } catch (error) {
      console.error('Error parsing aitarot_user JSON:', error);
    }
  }
  
  // 回退到旧的user键（向后兼容）
  const userJson = localStorage.getItem('user');
  if (!userJson) {
    console.log('No user found in localStorage');
    return null;
  }
  
  try {
    const user = JSON.parse(userJson);
    console.log('Parsed user from old storage:', user);
    
    if (user && user.email) {
      console.log('Valid user found:', user.id, user.email);
      return user;
    } else {
      console.log('User object found but missing email:', user);
      return null;
    }
  } catch (error) {
    console.error('Error parsing user JSON:', error);
    return null;
  }
};

// Check if user is logged in
export const isLoggedIn = () => {
  return !!getCurrentUser();
};

// Update syncReadingsWithBackend to ensure user is initialized first
export const syncReadingsWithBackend = async (user) => {
  if (!user || !user.id) return { success: false, message: 'No user data provided' };

  try {
    // First ensure user is initialized in backend
    const initResult = await initializeUser(user);
    if (!initResult.success) {
      throw new Error('Failed to initialize user in database');
    }

    // Fetch all readings from DB
    const dbResponse = await fetch(`${getBackendUrl()}/login/getall?user_id=${user.id}`);
    if (!dbResponse.ok) {
      throw new Error(`Server error: ${dbResponse.status}`);
    }
    
    const dbData = await dbResponse.json();
    const dbReadings = dbData.success ? dbData.readings : [];
    
    // Get local readings
    const localReadings = getUserReadings();
    
    // Create a map of DB readings by ID
    const dbReadingsMap = new Map();
    dbReadings.forEach(reading => {
      dbReadingsMap.set(reading.id, reading);
    });
    
    // Check each local reading
    for (const localReading of localReadings) {
      const dbReading = dbReadingsMap.get(localReading.id);
      
      // If reading doesn't exist in DB or local version is newer, upload to DB
      if (!dbReading || new Date(localReading.timestamp) > new Date(dbReading.timestamp)) {
        console.log(`Uploading newer local reading to DB: ${localReading.id}`);
        const sanitizedReading = sanitizeReadingForDB({
          ...localReading,
          userId: user.id,
          isAnonymous: false
        });
        await putReading(user.id, sanitizedReading);
      }
    }
    
    // Update local cache with final DB state
    await updateLocalCacheFromDB(user.id);
    
    // Notify components about updated readings
    notifyAuthStateChange();
    
    return { success: true, message: 'Readings synchronized successfully' };
  } catch (error) {
    console.error('Error syncing readings:', error);
    return { success: false, message: error.message };
  }
};

// Update getAllReadingsFromDB to handle errors better
export const getAllReadingsFromDB = async () => {
  const user = getCurrentUser();
  if (!user || !user.id) {
    console.log('No user found in getCurrentUser');
    return { success: false, message: 'No user logged in' };
  }

  try {
    console.log('Fetching readings for user:', user.id);
    
    // Update local cache from DB first to ensure we have latest data
    await updateLocalCacheFromDB(user.id);
    
    // Return readings from local storage
    const userReadings = getUserReadings();
    return {
      success: true,
      readings: userReadings,
      source: 'synchronized'
    };
  } catch (error) {
    console.error('Error fetching readings from DB:', error);
    // Fallback to local storage on error
    const localReadings = getUserReadings();
    return { 
      success: true, 
      readings: localReadings,
      source: 'local_storage',
      localOnly: true
    };
  }
};

// Add function to sync local readings with DB on login
export const syncReadingsOnLogin = async (userId) => {
  try {
    console.log('Syncing readings on login for user:', userId);
    
    // Get local readings
    const localReadings = getUserReadings();
    
    // Fetch all readings from DB
    const dbResponse = await fetch(`${getBackendUrl()}/login/getall?user_id=${userId}`);
    if (!dbResponse.ok) {
      throw new Error(`Server error: ${dbResponse.status}`);
    }
    
    const dbData = await dbResponse.json();
    const dbReadings = dbData.success ? dbData.readings : [];
    
    // Create maps for both sets of readings
    const dbReadingsMap = new Map();
    dbReadings.forEach(reading => {
      dbReadingsMap.set(reading.id, reading);
    });
    
    // Check each local reading
    for (const localReading of localReadings) {
      const dbReading = dbReadingsMap.get(localReading.id);
      
      // If reading doesn't exist in DB or local version is newer, upload to DB
      if (!dbReading || new Date(localReading.timestamp) > new Date(dbReading.timestamp)) {
        console.log(`Uploading newer local reading to DB: ${localReading.id}`);
        await putReading(userId, {
          ...sanitizeReadingForDB(localReading),
          userId: userId,
          isAnonymous: false
        });
      }
    }
    
    // Update local cache with final DB state
    await updateLocalCacheFromDB(userId);
    
    // Notify components about updated readings
    notifyAuthStateChange();
    
    return { success: true, message: 'Readings synchronized on login' };
  } catch (error) {
    console.error('Error syncing readings on login:', error);
    return { success: false, message: error.message };
  }
};

// 更新登出函数以清除所有用户数据
export const signOut = () => {
  try {
    const user = getCurrentUser();
    
    // 清除所有认证数据
    localStorage.removeItem('user'); // 旧键
    localStorage.removeItem('aitarot_user'); // 新键
    
    if (user && user.id) {
      // 清除用户相关数据
      localStorage.removeItem(`readings_${user.id}`);
    }
    
    // 强制刷新应用状态
    notifyAuthStateChange();
    
    console.log('用户登出成功');
    return true;
  } catch (error) {
    console.error('登出时发生错误:', error);
    return false;
  }
};

// Helper function to update local cache from DB
const updateLocalCacheFromDB = async (userId) => {
  try {
    // Fetch readings from database
    const response = await fetch(`${getBackendUrl()}/login/getall?user_id=${userId}`);
    
    if (!response.ok) {
      throw new Error(`Error fetching readings: ${response.status}`);
    }
    
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Server returned non-JSON response');
    }
    
    const data = await response.json();
    
    if (data.success && data.readings) {
      // Get current local readings
      const localReadingsJson = localStorage.getItem(`readings_${userId}`);
      const localReadings = localReadingsJson ? JSON.parse(localReadingsJson) : [];
      
      // Create a map of readings by ID for easy access
      const readingsMap = new Map();
      
      // First add all DB readings to the map
      data.readings.forEach(reading => {
        // Parse stored JSON fields
        reading.history = reading.history || [];
        reading.character = reading.character || null;
        readingsMap.set(reading.id, reading);
      });
      
      // Then check local readings and only keep newer versions based on timestamp
      localReadings.forEach(localReading => {
        const dbReading = readingsMap.get(localReading.id);
        
        if (!dbReading) {
          // Local reading doesn't exist in DB, add it
          readingsMap.set(localReading.id, localReading);
        } else {
          // Compare timestamps to decide which to keep
          const localTime = new Date(localReading.timestamp).getTime();
          const dbTime = new Date(dbReading.timestamp).getTime();
          
          // Use local reading if it's newer
          if (localTime > dbTime) {
            readingsMap.set(localReading.id, localReading);
          }
        }
      });
      
      // Convert map back to array and save to local storage
      const mergedReadings = Array.from(readingsMap.values());
      localStorage.setItem(`readings_${userId}`, JSON.stringify(mergedReadings));
      
      console.log(`Synchronized ${mergedReadings.length} readings in local cache`);
    }
  } catch (error) {
    console.error('Error updating local cache from DB:', error);
    // Don't throw the error - this is a background sync operation
  }
}; 